import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { vossSubscriptionItemTerminationTiming, VossSubscriptionItemTerminationTimingText } from "../../../../Constants/VOSS/vossSubscriptionItemTerminationTiming";
import { RadioButtons } from "../../../FormComponents/RadioButtons";
import SkynetForm from "../../../Fundamentals/Form/SkynetForm";
import helpers from "../../../../Utils/helpers";
import useUserAccess from "../../../../Hooks/useUserAccess";
import { UserRoles } from "../../../../Constants/userRoles";
import { Dropdown } from "../../../FormComponents/Dropdown";
import UserSearchNew from "../../../Search/UserSearchNew";
import { TerminationConfigs, TerminationTypes } from "../../../../Constants/VOSS/Termination/TerminationConfigs";
import { VossCreditKind, VossCreditKindText } from "../../../../Constants/VOSS/vossCreditKind";
import { VossCreditBehaviour, VossCreditBehaviourText } from "../../../../Constants/VOSS/vossCreditBehaviour";
import { FormInput } from "../../../FormComponents/Input/Input";
import subscriptionUtils from "../../../../Utils/subscriptionUtils";

export default function SubscriptionItemTerminationForm({ subscription, form: { data }, onChange }) {
    const { t } = useTranslation();
    const { hasAdminAccess, hasReadWriteAccess } = useUserAccess();

    const [orderType, setOrderType] = useState(data.orderType || 'ordinary');

    const orderTypeOptions = [
        { id: 'ordinary', label: t('confirmordermodal.ordinary') },
        { id: 'other', label: t('confirmordermodal.orderforother'), disabled: !hasReadWriteAccess(UserRoles.OrderForOther) },
        { id: 'partner', label: t('confirmordermodal.partner') },
        { id: 'web', label: t('confirmordermodal.web') },
        { id: 'onlineSales', label: t('confirmordermodal.onlineorder') }
    ];

    useEffect(() => {
        if (orderType !== 'other' && data.salesPersonEmail) {
            onChange('salesPersonEmail', null);
        }
    }, [orderType, data.salesPersonEmail, onChange]);

    useEffect(() => {
        if (data.orderType !== orderType) {
            onChange('orderType', orderType);
        }
    }, [orderType, data.orderType, onChange]);

    const creditDisabled = data.terminationType != TerminationTypes.Immediately;

    const timingOptions = [
        { id: 'termination-timing-1', label: subscriptionUtils.hasEvergreenBindingPeriod(subscription) ? t('subscriptiontermination.endOfNextMonthTermination') : `${t(VossSubscriptionItemTerminationTimingText[vossSubscriptionItemTerminationTiming.AtBindingPeriodEnd])} (${helpers.formatDate(subscription.periodEnd)})`, value: TerminationTypes.CurrentPeriodEnd },
        ...(hasAdminAccess(UserRoles.SubscriptionAdmin) ? [{ id: 'termination-timing-3', label: t(VossSubscriptionItemTerminationTimingText[vossSubscriptionItemTerminationTiming.Immediately]), value: TerminationTypes.Immediately }] : [])
    ];

    const creditKindOptions = [
        { id: 'credit-kind-1', label: t(VossCreditKindText[VossCreditKind.Normal]), value: VossCreditKind.Normal },
        { id: 'credit-kind-2', label: t(VossCreditKindText[VossCreditKind.Internal]), value: VossCreditKind.Internal }
    ]

    const creditBehaviourOptions = [
        { id: 'credit-behaviour-1', label: t(VossCreditBehaviourText[VossCreditBehaviour.Full]), value: VossCreditBehaviour.Full },
        { id: 'credit-behaviour-2', label: t(VossCreditBehaviourText[VossCreditBehaviour.Prorate]), value: VossCreditBehaviour.Prorate },
        { id: 'credit-behaviour-3', label: t(VossCreditBehaviourText[VossCreditBehaviour.None]), value: VossCreditBehaviour.None }
    ]

    const onTerminationTimingChange = (value) => {
        const config = TerminationConfigs[value];

        Object.entries(config).forEach(([key, val]) => {
            onChange(key, val);
        });
    }

    return <SkynetForm>
        <fieldset className="d-grid col-12 gap-12">
            <Dropdown
                label={t('confirmordermodal.orderType')}
                options={orderTypeOptions}
                value={orderType}
                onChange={setOrderType}
                property="label"
                required
            />
            {hasReadWriteAccess(UserRoles.OrderForOther) && orderType === 'other' && (
                <UserSearchNew
                    label={t('confirmordermodal.orderforother')}
                    onChange={(user) => onChange('salesPersonEmail', user?.email || null)}
                    tooltip={t('confirmordermodal.orderforothertooltip')}
                    value={data.salesPersonEmail}
                />
            )}
        </fieldset>
        <fieldset className="d-grid col-12 gap-12">
            <div className="form-group mb-0 required d-flex flex-column align-items-start">
                <RadioButtons label={t("subscriptiontermination.timeoftermination")} items={timingOptions} value={data.terminationType} onChange={onTerminationTimingChange} />
            </div>
        </fieldset>
        {!creditDisabled && <fieldset className={`d-grid col-12 gap-12`}>
            <label className='bold'>{t("subscriptiontermination.credit")}</label>
            <div className="form-group mb-0 required d-flex flex-column align-items-start">
                <RadioButtons label={t("subscriptiontermination.creditkind")} items={creditKindOptions} value={data.creditKind} onChange={(value) => onChange("creditKind", value)} />
            </div>
            <div className="form-group mb-0 required d-flex flex-column align-items-start">
                <RadioButtons label={t("subscriptiontermination.creditbehaviour")} items={creditBehaviourOptions} value={data.behaviour} onChange={(value) => onChange("behaviour", value)} />
            </div>
            <FormInput
                label={t("subscriptiontermination.invoicetext")}
                placeholder={t("subscriptiontermination.textshownoninvoice")}
                value={data.invoiceLineTexts}
                onChange={(input) => onChange("invoiceLineTexts", input)} />
        </fieldset>}
    </SkynetForm>
}